import React, { useState, useEffect } from 'react';
import {
  FaTachometerAlt,
  FaUsers,
  FaBox,
  FaShoppingCart,
  FaChartBar,
  FaCog,
  FaFileInvoiceDollar,
  FaCalendarAlt,
  FaLifeRing,
  FaSignOutAlt,
  FaTimes,
  FaBars,
  FaHome,
  FaIdBadge,
  FaChevronDown,
  FaChevronRight,
  FaRegQuestionCircle,
  FaDownload,
  FaPlus,
  FaRedoAlt,
  FaFire,
  FaThLarge,
  FaImage,
  FaBullhorn
} from 'react-icons/fa';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { useLocation } from 'react-router-dom';

const Sidebar = ({ sidebarOpen, darkMode, toggleSidebar, collapsed, toggleCollapsed, onNavigate }) => {
  const location = useLocation();
  const [hoveredItem, setHoveredItem] = useState(null);
  const [activeItem, setActiveItem] = useState('dashboard');
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [openSubmenus, setOpenSubmenus] = useState({
    packages: false,
    houses: true,
    analytics: false,
    settings: false
  });

  // Check device size
  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Update active item based on route
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/users')) setActiveItem('users');
    else if (path.includes('/categories')) {
      setActiveItem('categories');
      setOpenSubmenus(prev => ({ ...prev, packages: true }));
    }
    else if (path.includes('/allhouses') || path.includes('/create-house')) {
      setActiveItem('houses');
      setOpenSubmenus(prev => ({ ...prev, houses: true }));
    }
    else if (path.includes('/plans')) setActiveItem('plans');
    else if (path.includes('/settings')) {
      setActiveItem('settings');
      setOpenSubmenus(prev => ({ ...prev, settings: true }));
    }
    else setActiveItem('dashboard');
  }, [location]);

  const toggleSubmenu = (menu) => {
    if (collapsed && !isMobile) return;
    setOpenSubmenus(prev => ({
      ...prev,
      [menu]: !prev[menu]
    }));
  };

  const menuItems = [
    {
      id: 'dashboard',
      icon: <FaTachometerAlt />,
      text: 'Dashboard',
      path: '/',
      description: 'Overview & analytics'
    },
    {
      id: 'users',
      icon: <FaUsers />,
      text: 'Users',
      path: '/users',
      description: 'Manage users'
    },
    {
      id: 'banners',
      icon: <FaImage />,
      text: 'Banners',
      path: '/banners',
      description: 'Manage Banners'
    },
    {
      id: 'categories',
      icon: <FaThLarge />,
      text: 'Categories',
      path: '/categories',
      description: 'Manage categories'
    },
    {
      id: 'houses',
      icon: <FaHome />,
      text: 'Houses',
      path: '/allhouses',
      description: 'Property management',
      subItems: [
        { id: 'allhouses', text: 'All Houses', path: '/allhouses', icon: '🏠' },
        { id: 'createhouse', text: 'Create House', path: '/create-house/new', icon: '➕' }
      ]
    },
    {
      id: 'broadcast',
      icon: <FaBullhorn />,
      text: 'Broadcast',
      path: '/broadcast',
      description: 'Send notifications & updates'
    },
    // { 
    //   id: 'plans', 
    //   icon: <FaFileInvoiceDollar />, 
    //   text: 'Plans', 
    //   path: '/plans',
    //   description: 'Subscription plans'
    // },
    // { 
    //   id: 'settings', 
    //   icon: <FaCog />, 
    //   text: 'Settings', 
    //   path: '/settings',
    //   description: 'System settings',
    //   subItems: [
    //     { id: 'general', text: 'General', path: '/settings/general', icon: '⚙️' },
    //     { id: 'security', text: 'Security', path: '/settings/security', icon: '🔒' },
    //     { id: 'notifications', text: 'Notifications', path: '/settings/notifications', icon: '🔔' }
    //   ]
    // },
  ];

  const handleItemClick = (item) => {
    if (item.subItems && !collapsed) {
      toggleSubmenu(item.id);
      if (!openSubmenus[item.id]) {
        onNavigate(item.path);
        setActiveItem(item.id);
      }
    } else {
      onNavigate(item.path);
      setActiveItem(item.id);
    }
  };

  const handleSubItemClick = (subItem, parentId) => {
    onNavigate(subItem.path);
    setActiveItem(subItem.id);

    if (isMobile) {
      toggleSidebar();
    }
  };

  const handleLogout = () => {
    sessionStorage.clear();
    window.location.href = '/';
  };

  // Check if a subitem is active
  const isSubItemActive = (parentId, subItems) => {
    return subItems?.some(subItem => {
      const path = location.pathname;
      return path.includes(subItem.path.split('/')[1]) || path === subItem.path;
    });
  };

  // Get active state for main items
  const isItemActive = (item) => {
    return activeItem === item.id || isSubItemActive(item.id, item.subItems);
  };

  return (
    <>
      {/* Mobile Overlay */}
      {sidebarOpen && isMobile && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 transition-opacity duration-300"
          onClick={toggleSidebar}
        ></div>
      )}

      {/* Sidebar */}
      <aside className={`
        ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        ${!isMobile && (collapsed ? 'w-20' : 'w-64')}
        ${isMobile ? 'w-64' : ''}
        fixed md:relative z-40
        h-screen
        transition-all duration-300 ease-in-out
        flex flex-col
        border-r ${darkMode ? 'border-gray-700' : 'border-red-100'}
        shadow-xl md:shadow-lg
        overflow-hidden
      `}>
        {/* Header */}
        <div className={`
          p-4 ${darkMode ? 'border-gray-700' : 'border-red-100'} 
          flex items-center 
          ${!isMobile && collapsed ? 'justify-center' : 'justify-between'}
          border-b
          bg-gradient-to-r ${darkMode ? 'from-gray-800 to-gray-900' : 'from-white to-red-50'}
        `}>
          {/* Logo */}
          {(!collapsed || isMobile) ? (
            <div className="flex items-center cursor-pointer group" onClick={() => onNavigate('/')}>
              <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center mr-3 shadow-lg group-hover:shadow-red-500/30 transition-all duration-300">
                <span className="text-white font-bold text-lg">A</span>
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-red-600 to-red-500 bg-clip-text text-transparent">
                  Admin
                </h1>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>v2.0 • Red Theme</p>
              </div>
            </div>
          ) : (
            <div
              className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center cursor-pointer mx-auto shadow-lg hover:shadow-red-500/30 transition-all duration-300"
              onClick={() => onNavigate('/')}
            >
              <span className="text-white font-bold text-xl">A</span>
            </div>
          )}

          {/* Close Button (Mobile only) */}
          {isMobile && (
            <button
              onClick={toggleSidebar}
              className={`p-1 rounded-lg transition-all duration-200 ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-red-100'}`}
              aria-label="Close sidebar"
            >
              <FaTimes className="text-lg" />
            </button>
          )}

          {/* Collapse Toggle Button (Desktop only) */}
          {!isMobile && (
            <button
              onClick={toggleCollapsed}
              className={`
                hidden md:flex p-1.5 rounded-lg transition-all duration-200
                ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-red-100'}
                ${collapsed ? 'absolute -right-3 top-6 bg-white dark:bg-gray-800 border shadow-lg' : ''}
              `}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {collapsed ? (
                <FiChevronRight className="text-lg text-red-500" />
              ) : (
                <FiChevronLeft className="text-lg text-red-500" />
              )}
            </button>
          )}
        </div>

        {/* Menu Items */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden p-2 md:p-3 scrollbar-thin">
          <ul className="space-y-1">
            {menuItems.map((item) => (
              <li key={item.id}>
                {/* Main Menu Item */}
                <div>
                  <button
                    onClick={() => handleItemClick(item)}
                    className={`
                      w-full flex items-center 
                      ${(!isMobile && collapsed) ? 'justify-center' : 'justify-between'} 
                      p-2.5 md:p-3 rounded-lg transition-all duration-200
                      group relative
                      ${isItemActive(item)
                        ? `${darkMode
                          ? 'bg-gradient-to-r from-red-600/20 to-red-600/10 text-red-400'
                          : 'bg-gradient-to-r from-red-50 to-red-100 text-red-600'
                        }`
                        : `${darkMode
                          ? 'hover:bg-gray-700/50 text-gray-300 hover:text-white'
                          : 'hover:bg-red-50 text-gray-600 hover:text-red-600'
                        }`
                      }
                    `}
                    onMouseEnter={() => (!isMobile && collapsed) && setHoveredItem(item.id)}
                    onMouseLeave={() => (!isMobile && collapsed) && setHoveredItem(null)}
                  >
                    <div className="flex items-center min-w-0">
                      <span className={`
                        ${(!isMobile && collapsed) ? 'text-xl' : 'text-lg'} 
                        transition-transform duration-200
                        group-hover:scale-110
                        ${isItemActive(item) ? 'text-red-500' : ''}
                      `}>
                        {item.icon}
                      </span>

                      {/* Text - hidden when collapsed on desktop */}
                      {(!collapsed || isMobile) && (
                        <div className="ml-3 text-left">
                          <span className="font-medium block">{item.text}</span>
                          <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'} hidden md:block`}>
                            {item.description}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Submenu arrow (only when not collapsed on desktop) */}
                    {(!collapsed || isMobile) && item.subItems && (
                      <span className="text-xs ml-2 transition-transform duration-200">
                        {openSubmenus[item.id] ?
                          <FaChevronDown className="text-red-500" /> :
                          <FaChevronRight className="text-red-500" />
                        }
                      </span>
                    )}

                    {/* Active indicator */}
                    {isItemActive(item) && !collapsed && (
                      <span className="w-1.5 h-8 bg-red-500 rounded-full absolute right-0 top-1/2 transform -translate-y-1/2"></span>
                    )}

                    {/* Tooltip for collapsed state */}
                    {!isMobile && collapsed && hoveredItem === item.id && (
                      <div className={`
                        absolute left-full ml-2 px-3 py-2 rounded-lg shadow-xl z-50
                        ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-800'}
                        whitespace-nowrap
                        border-l-4 border-red-500
                      `}>
                        <div className="font-medium">{item.text}</div>
                        <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {item.description}
                        </div>
                      </div>
                    )}
                  </button>

                  {/* Sub-items (only when not collapsed or on mobile) */}
                  {(!collapsed || isMobile) && item.subItems && openSubmenus[item.id] && (
                    <div className={`
                      mt-1 ml-6 md:ml-8 pl-3 
                      border-l-2 ${darkMode ? 'border-gray-700' : 'border-red-200'} 
                      space-y-1
                      animate-slideDown
                    `}>
                      {item.subItems.map((subItem) => {
                        const isActive = location.pathname === subItem.path ||
                          location.pathname.includes(subItem.path.split('/')[1]);

                        return (
                          <button
                            key={subItem.id}
                            onClick={() => handleSubItemClick(subItem, item.id)}
                            className={`
                              w-full flex items-center justify-start p-2 rounded-lg 
                              transition-all duration-200 text-sm
                              group relative
                              ${isActive
                                ? `${darkMode
                                  ? 'bg-gray-700 text-red-400'
                                  : 'bg-red-50 text-red-600'
                                } font-medium`
                                : `${darkMode
                                  ? 'hover:bg-gray-700 text-gray-300 hover:text-white'
                                  : 'hover:bg-red-50 text-gray-600 hover:text-red-600'
                                }`
                              }
                            `}
                          >
                            <span className="mr-2 text-base">{subItem.icon || '•'}</span>
                            <span className="flex-1 text-left">{subItem.text}</span>
                            {isActive && (
                              <span className="w-1.5 h-1.5 bg-red-500 rounded-full ml-2"></span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>

          {/* Divider */}
          <div className={`my-4 border-t ${darkMode ? 'border-gray-700' : 'border-red-100'}`}></div>

          {/* Additional Links */}
          {/* <div className="space-y-1">
            <button
              className={`
                w-full flex items-center ${(!isMobile && collapsed) ? 'justify-center' : 'justify-start'} 
                p-2.5 md:p-3 rounded-lg transition-all duration-200
                ${darkMode ? 'hover:bg-gray-700/50 text-gray-300' : 'hover:bg-red-50 text-gray-600'}
              `}
              onMouseEnter={() => (!isMobile && collapsed) && setHoveredItem('help')}
              onMouseLeave={() => (!isMobile && collapsed) && setHoveredItem(null)}
            >
              <FaLifeRing className={`${(!isMobile && collapsed) ? 'text-xl' : 'text-lg'}`} />
              {(!collapsed || isMobile) && <span className="ml-3">Help & Support</span>}
              
              {!isMobile && collapsed && hoveredItem === 'help' && (
                <div className={`absolute left-full ml-2 px-3 py-2 rounded-lg shadow-xl z-50 ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                  Help & Support
                </div>
              )}
            </button>

            <button
              className={`
                w-full flex items-center ${(!isMobile && collapsed) ? 'justify-center' : 'justify-start'} 
                p-2.5 md:p-3 rounded-lg transition-all duration-200
                ${darkMode ? 'hover:bg-gray-700/50 text-gray-300' : 'hover:bg-red-50 text-gray-600'}
              `}
              onMouseEnter={() => (!isMobile && collapsed) && setHoveredItem('backup')}
              onMouseLeave={() => (!isMobile && collapsed) && setHoveredItem(null)}
            >
              <FaDownload className={`${(!isMobile && collapsed) ? 'text-xl' : 'text-lg'}`} />
              {(!collapsed || isMobile) && <span className="ml-3">Backup</span>}
              
              {!isMobile && collapsed && hoveredItem === 'backup' && (
                <div className={`absolute left-full ml-2 px-3 py-2 rounded-lg shadow-xl z-50 ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                  Backup
                </div>
              )}
            </button>
          </div> */}
        </nav>

        {/* Logout Button */}
        <div className={`
          p-3 border-t ${darkMode ? 'border-gray-700' : 'border-red-100'}
          ${!isMobile && collapsed ? 'text-center' : ''}
        `}>
          <button
            onClick={handleLogout}
            className={`
              w-full flex items-center ${!isMobile && collapsed ? 'justify-center' : 'justify-start'} 
              p-2.5 rounded-lg transition-all duration-200
              ${darkMode ? 'hover:bg-gray-700 text-red-400' : 'hover:bg-red-100 text-red-600'}
              group relative
            `}
            onMouseEnter={() => (!isMobile && collapsed) && setHoveredItem('logout')}
            onMouseLeave={() => (!isMobile && collapsed) && setHoveredItem(null)}
          >
            <FaSignOutAlt className={`${!isMobile && collapsed ? 'text-xl' : 'text-lg'} transition-transform group-hover:translate-x-1`} />
            {(!collapsed || isMobile) && <span className="ml-3 font-medium">Logout</span>}

            {/* Tooltip for collapsed state */}
            {!isMobile && collapsed && hoveredItem === 'logout' && (
              <div className={`absolute left-full ml-2 px-3 py-2 rounded-lg shadow-xl z-50 ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                Logout
              </div>
            )}
          </button>
        </div>

        {/* User Profile */}
        <div className={`
          p-3 border-t ${darkMode ? 'border-gray-700' : 'border-red-100'}
          ${!isMobile && collapsed ? 'text-center' : ''}
          bg-gradient-to-r ${darkMode ? 'from-gray-800 to-gray-900' : 'from-red-50 to-white'}
        `}>
          {!isMobile && collapsed ? (
            <div className="relative">
              <img
                src="https://ui-avatars.com/api/?name=AU&background=dc2626&color=fff&bold=true"
                alt="Admin"
                className="w-10 h-10 rounded-full mx-auto cursor-pointer border-2 border-red-500 hover:scale-110 transition-transform duration-200"
                onMouseEnter={() => setHoveredItem('profile')}
                onMouseLeave={() => setHoveredItem(null)}
                onClick={() => onNavigate('/profile')}
              />
              <div className="absolute bottom-0 right-1/2 translate-x-1/2 translate-y-1/2 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-gray-800"></div>

              {/* Profile tooltip */}
              {hoveredItem === 'profile' && (
                <div className={`
                  absolute left-full bottom-0 ml-2 px-3 py-2 rounded-lg shadow-xl z-50
                  ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-800'}
                  whitespace-nowrap
                  border-l-4 border-red-500
                `}>
                  <div className="text-sm font-medium">Admin User</div>
                  <div className="text-xs opacity-75">admin@example.com</div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center cursor-pointer group" onClick={() => onNavigate('/profile')}>
              <div className="relative">
                <img
                  src="https://ui-avatars.com/api/?name=Admin+User&background=dc2626&color=fff&bold=true"
                  alt="Admin User"
                  className="w-10 h-10 rounded-full border-2 border-red-500 group-hover:scale-110 transition-transform duration-200"
                />
                <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-gray-800"></div>
              </div>
              <div className="ml-3 min-w-0">
                <h4 className="font-semibold text-sm truncate">Admin User</h4>
                <p className={`text-xs truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  admin@example.com
                </p>
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Floating Toggle Button for Mobile */}
      {!sidebarOpen && isMobile && (
        <button
          onClick={toggleSidebar}
          className="fixed bottom-4 left-4 md:hidden z-50 bg-gradient-to-r from-red-500 to-red-600 text-white p-4 rounded-full shadow-lg hover:shadow-red-500/50 transition-all duration-200 hover:scale-110"
          aria-label="Open menu"
        >
          <FaBars className="text-xl" />
        </button>
      )}
    </>
  );
};

export default Sidebar;